import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { PLAYBACK_SESSION_CHECK_MUTATION } from "@/apollo/queries/playback";
import { useAddResumeMutation, useFullscreenApi, usePlaybackSessionCloseMutation } from "@/hooks";
import config from "@/lib/config";
import debugLog from "@/lib/debugLog";
import { PlalaBeaconError, UNSUPPORTED_SCHEME_ERROR_MESSAGE } from "@/lib/errors";
import getVisitorId from "@/lib/getVisitorId";
import maybeIOS from "@/lib/maybeIOS";
import { liveStatus } from "@/lib/metaUtil";
import { useApolloClient } from "@apollo/client";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
const beaconInterval = 30 * 1000;
export function usePlayerEventHandler(user, userLoading, deviceInfo, player, meta, entitlements, playbackAuth, playToken, displayOverlay, canplay) {
  var _playbackAuthRef$curr3, _playbackAuthRef$curr5;
  let callbacks = arguments.length > 10 && arguments[10] !== undefined ? arguments[10] : {};
  const client = useApolloClient();
  const {
    0: handled,
    1: setHandled
  } = useState(false);
  const resumeSending = useRef(false);
  const mounted = useRef(false);
  const metaRef = useRef();
  const vuid = useRef();
  const playingIntervalId = useRef();
  const beaconIntervalId = useRef();
  const seeking = useRef(false);
  const playTimePercentage = useRef(0);
  const playbackAuthRef = useRef();
  const playTokenRef = useRef();
  const fsAPI = useFullscreenApi();
  const [addResumeMutation, {
    loading
  }] = useAddResumeMutation();
  const [sessionCloseMutation] = usePlaybackSessionCloseMutation();
  const router = useRouter();
  playbackAuthRef.current = playbackAuth;
  playTokenRef.current = playToken;
  useEffect(() => {
    const handler = () => {
      setHandled(false);
      sendResume();
    };
    router.events.on("routeChangeStart", handler);
    return () => {
      router.events.off("routeChangeStart", handler);
    };
  }, []);
  const logTrack = useCallback(function (event) {
    var _playbackAuthRef$curr, _user$currentProfile;
    let additionalParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    // log_params:
    //   a: 4
    //   aid: 17
    //   cp: 6
    //   dc: 1
    //   fl: 1
    //   media_id: 8
    //   meta_id: 195
    //   psgk: "default"
    //   psid: "bf57765ffa0b4c069d61e1eedd05f675"
    //   service: "spod"
    //   uid: 17
    const meta = metaRef.current;
    const logParams = (_playbackAuthRef$curr = playbackAuthRef.current) === null || _playbackAuthRef$curr === void 0 ? void 0 : _playbackAuthRef$curr.log_params;
    if (["pause", "ended", "dismiss", "dispose", "error"].includes(event)) {
      clearInterval(playingIntervalId.current);
      playingIntervalId.current = null;
    }
    const episodeType = (meta => {
      if (meta.is_live) {
        if (liveStatus(meta)) {
          return "live";
        } else if (meta.catch_up_flag) {
          return "catchup";
        }
      }
      return "vod";
    })(meta);
    const params = _objectSpread(_objectSpread({
      front_id: config.frontId,
      episode_type: episodeType,
      s: parseInt(player === null || player === void 0 ? void 0 : player.currentTime(), 10),
      ts: +new Date(),
      vuid: vuid.current
    }, logParams), additionalParams);
    if (event != "beacon") {
      params.event = event;
    }
    if (["pause", "dismiss", "ended", "dispose"].includes(event)) {
      params.pt = player === null || player === void 0 ? void 0 : player.currentTime();
    }
    if (user !== null && user !== void 0 && (_user$currentProfile = user.currentProfile) !== null && _user$currentProfile !== void 0 && _user$currentProfile.id) {
      params.profile_id = user.currentProfile.id;
    }
    if (user && user.test_flg) {
      params.fl = 1;
    }
    const rightIds = entitlements.map(entitle => entitle.right_id);
    if (rightIds.length > 0) {
      params.right_id = rightIds.join(",");
    }
    const url = (() => {
      if (event === "beacon") {
        return `${config.logTrack.url}/beacon`;
      } else {
        return `${config.logTrack.url}/track`;
      }
    })();
    debugLog(event, params);
    const qs = new URLSearchParams(params);
    return fetch(`${url}?${qs}`).catch(e => console.error(e));
  }, [user, meta, entitlements, player, playbackAuthRef.current, vuid]);
  const plalaBeacon = useCallback(async () => {
    if (playTokenRef.current) {
      const {
        url,
        clientId
      } = config.plalaBeacon;
      const body = JSON.stringify({
        play_token: playTokenRef.current,
        stop_position: Math.floor(player === null || player === void 0 ? void 0 : player.currentTime())
      });
      fetch(url, {
        method: "POST",
        mode: "cors",
        body,
        headers: {
          "Content-Type": "application/json",
          "X-Client": clientId
        }
      }).then(res => res.json()).then(res => {
        // console.log(res);
        // TODO: レスポンスをみて再生を止める必要がある
        // 1002103
        // 1002201
        // {
        //   "status": "NG",
        //   "errorno": "1002103"
        // }
        const stopStatus = ["1002103", "1002201"];
        if (res.status === "NG" && stopStatus.includes(res === null || res === void 0 ? void 0 : res.errorno)) {
          player === null || player === void 0 || player.dispose();
          const error = new PlalaBeaconError(null, res.errorno);
          displayOverlay({
            reason: "error",
            error
          });
        }
      }).catch(e => {
        console.error(e);
        player === null || player === void 0 || player.dispose();
        const error = new PlalaBeaconError(null, "9999");
        displayOverlay({
          reason: "error",
          error
        });
        window.plugin.fireError(9999, error.message + "（バリュープランポーリングエラーの可能性あり）");
      });
    }
  }, [player, playTokenRef.current, displayOverlay]);
  const sendResume = useCallback(async () => {
    var _playbackAuthRef$curr2;
    if (resumeSending.current) return;
    if (loading) return;
    // isLiveだけだとfirstplayの時にfalseで返ってくるのでdurationが取れるかどうかも判定する
    if (player && isNaN(player === null || player === void 0 ? void 0 : player.duration())) return;
    if (player && player !== null && player !== void 0 && player.isLive()) return;
    const meta = metaRef.current;
    const psid = (_playbackAuthRef$curr2 = playbackAuthRef.current) === null || _playbackAuthRef$curr2 === void 0 || (_playbackAuthRef$curr2 = _playbackAuthRef$curr2.log_params) === null || _playbackAuthRef$curr2 === void 0 ? void 0 : _playbackAuthRef$curr2.psid;
    if (user && meta && player && psid) {
      var _meta$tvShow;
      resumeSending.current = true;
      const resume = player !== null && player !== void 0 && player.isLive() ? -1 : parseInt(player === null || player === void 0 ? void 0 : player.currentTime(), 10);
      const reachToEnd = player !== null && player !== void 0 && player.ended() ? true : false;
      await addResumeMutation({
        variables: {
          resume: resume,
          metaId: Number(meta.id),
          groupedMetaId: meta.tvShow ? Number((_meta$tvShow = meta.tvShow) === null || _meta$tvShow === void 0 ? void 0 : _meta$tvShow.id) : null,
          psid,
          reachToEnd
        }
      });
      resumeSending.current = false;
    }
  }, [resumeSending, loading, user, player, playbackAuthRef.current]);
  const sessionCheck = useCallback(() => {
    client.mutate({
      mutation: PLAYBACK_SESSION_CHECK_MUTATION,
      variables: {
        sessionId: playbackAuthRef.current.playback_session_id
      }
    }).then(_ref => {
      var _data$playbackSession;
      let {
        data
      } = _ref;
      if (!(data !== null && data !== void 0 && (_data$playbackSession = data.playbackSessionCheck) !== null && _data$playbackSession !== void 0 && _data$playbackSession.result)) {
        window.location.reload();
      }
    }).catch(e => {
      // サーバーが落ちた場合、networkErrorで返ってくる
      if (e.networkError) {
        window.plugin.fireError(99999, UNSUPPORTED_SCHEME_ERROR_MESSAGE[99999]);
      }
      console.log(e);
      window.location.reload();
    });
  }, [(_playbackAuthRef$curr3 = playbackAuthRef.current) === null || _playbackAuthRef$curr3 === void 0 ? void 0 : _playbackAuthRef$curr3.playback_session_id]);
  const sessionClose = useCallback(async () => {
    var _playbackAuthRef$curr4;
    if (playbackAuth.current && (_playbackAuthRef$curr4 = playbackAuthRef.current) !== null && _playbackAuthRef$curr4 !== void 0 && _playbackAuthRef$curr4.playback_session_id) {
      await sessionCloseMutation({
        variables: {
          sessionId: playbackAuthRef.current.playback_session_id
        }
      });
    }
  }, [(_playbackAuthRef$curr5 = playbackAuthRef.current) === null || _playbackAuthRef$curr5 === void 0 ? void 0 : _playbackAuthRef$curr5.playback_session_id]);

  // イベントハンドリング
  // beacon 視聴履歴、ブックマーク
  // paraviをみるとon playでplaybacksession checkをコールして invalid_grantになったらリロードしてた
  // セッションを取得した直後にチェックが必要なのか？
  const eventHandler = useCallback(() => {
    function execCallback(event) {
      if (callbacks[event]) {
        callbacks[event]();
      }
    }
    player === null || player === void 0 || player.on("loadstart", () => {
      // 既存コードではEdgeでDRMがある場合playreadyに対応しているか確認している
      execCallback("loadstart");
    });
    player === null || player === void 0 || player.on("canplay", () => {
      // 既存コードではvideoタグにtitleを設定してstate canplayをtrueにしている
      execCallback("canplay");
    });
    player === null || player === void 0 || player.on("firstplay", () => {
      logTrack("firstplay");
      sendResume();
      execCallback("firstplay");
    });
    player === null || player === void 0 || player.on("play", () => {
      // beacon startを送信
      // audienceLog Playを送信
      logTrack("play");
      execCallback("play");
      // playback/sessionをチェック
      sessionCheck();
    });
    player === null || player === void 0 || player.on("playing", () => {
      // beacon playingを30secごとに送信
      if (!playingIntervalId.current) {
        playingIntervalId.current = setInterval(() => {
          logTrack("beacon");
        }, beaconInterval);
      }
      if (!beaconIntervalId.current) {
        beaconIntervalId.current = setInterval(() => {
          plalaBeacon();
        }, beaconInterval);
      }
      execCallback("playing");
      // audienceLog Playingを30secごとに送信
    });

    player === null || player === void 0 || player.on("pause", () => {
      // 後追いなしの場合は停止させない
      if (player !== null && player !== void 0 && player.isLive() && meta.is_live && !meta.start_over_flag) {
        player === null || player === void 0 || player.play();
      } else if (!(player !== null && player !== void 0 && player.ended()) && !(player !== null && player !== void 0 && player.seeking())) {
        // audienceLog Pauseを送信
        // beacon Pause
        logTrack("pause");
        sendResume();
      }
      execCallback("pause");
    });
    player === null || player === void 0 || player.on("timeupdate", () => {
      // currentTimeがdurationの何％かチェックして
      // 25%,50%,75%のときにmilestoneイベントを送る
      // シークでもどった時には発火しないようにする
      // シークで一気に進んだ場合は全部送る
      const currentTime = player === null || player === void 0 ? void 0 : player.currentTime();
      const duration = player === null || player === void 0 ? void 0 : player.duration();
      [25, 50, 75].filter(per => per > playTimePercentage.current).forEach(per => {
        // 複数回実行されないために、ここでもう一度filterで使った条件をいれる
        if (per > playTimePercentage.current && currentTime > duration * (per / 100)) {
          playTimePercentage.current = per;
          logTrack("milestone", {
            percentage: per
          });
        }
      });
      execCallback("timeupdate");
    });
    player === null || player === void 0 || player.on("seeking", () => {
      // 既存ではwaitingRetryという処理を実行していた
      // 今回必要になるか不明
      if (!seeking.current) {
        seeking.current = true;
        logTrack("seekStart");
      }
      debugLog("seeking");
      execCallback("seeking");
    });
    player === null || player === void 0 || player.on("seeked", () => {
      // 既存では以下の問題があったようだがどうなのか
      // iOSのfiarplayだとシーク後に音声のみになってしまう
      seeking.current = false;
      logTrack("seekEnd");
      debugLog("seeked");
      execCallback("seeked");
    });
    player === null || player === void 0 || player.on("waiting", () => {
      // 既存では以下の問題があったようだがどうなのか
      // AESのライブがm3u8にEndListがなく、keyリクエストが404になるだけで再生が止められないので
      // LIVEでwaitingが発火したときに配信期間が過ぎていたら再生を止める
      debugLog("waiting");
      execCallback("waiting");
    });
    player === null || player === void 0 || player.on("renditionswitch", () => {
      debugLog("renditionswitch", player === null || player === void 0 ? void 0 : player.rendition());
    });
    player === null || player === void 0 || player.on("ended", () => {
      // beacon End
      logTrack("ended");
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      // audienceLog Endを送信
      sendResume();
      execCallback("ended");
    });
    player === null || player === void 0 || player.on("suspend", () => {
      logTrack("suspend");
      execCallback("suspend");
      debugLog("suspend");
    });
    player === null || player === void 0 || player.on("dispose", () => {
      logTrack("dispose");
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      execCallback("dispose");
      sendResume();
    });
    player === null || player === void 0 || player.on("error", () => {
      clearInterval(playingIntervalId.current);
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      logTrack("error");
      sendResume();
      sessionClose();
      execCallback("error");
      // beacon stopを送信
      // audienceLog Stopを送信
    });
  }, [player, callbacks, playbackAuthRef.current, plalaBeacon]);
  useEffect(() => {
    mounted.current = true;
    getVisitorId().then(id => vuid.current = id);
    // watchページないで別のwatchページに遷移した時に、ページ遷移する前にmetaだけ更新されてレジュームが適切に送信されないので
    // refでメタをもっておいて更新されないようにする
    metaRef.current = meta;
    return () => {
      mounted.current = false;
    };
  }, [meta.id]);
  useEffect(() => {
    let hidden, visibilityChange;
    if (typeof document.hidden !== "undefined") {
      hidden = "hidden";
      visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
      hidden = "msHidden";
      visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }
    const handlePageHide = () => {
      sendResume();
    };
    const handleVisibilityChange = () => {
      if (document[hidden]) {
        handlePageHide();
      } else {
        // 再生中の場合、playback/sessionをチェック
        if (playingIntervalId.current) {
          sessionCheck();
        }
      }
    };
    const handleBeforeUnload = () => {
      logTrack("dismiss");
    };
    const handleFullscreenChange = () => {
      if (player && !(player !== null && player !== void 0 && player.ended())) {
        if (player !== null && player !== void 0 && player.isFullscreen()) {
          logTrack("fullscreen");
        } else {
          logTrack("exitFullscreen");
        }
      }
    };
    window.addEventListener(visibilityChange, handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    const isIOS = maybeIOS(deviceInfo);
    if (isIOS || deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isMac && deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isSafari) {
      window.addEventListener("pagehide", handlePageHide);
      router.beforePopState(_ref2 => {
        let {
          url,
          as,
          options
        } = _ref2;
        return close;
      });
    }
    document.addEventListener(fsAPI.fullscreenchange, handleFullscreenChange);
    return () => {
      clearInterval(playingIntervalId.current);
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener(visibilityChange, handleVisibilityChange);
      if (isIOS || deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isMac && deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isSafari) {
        window.removeEventListener("pagehide", handlePageHide);
        router.beforePopState(() => true);
      }
      document.removeEventListener(fsAPI.fullscreenchange, handleFullscreenChange);
    };
  }, [deviceInfo, player, playbackAuthRef.current, fsAPI]);
  useEffect(() => {
    if (!handled && player && playbackAuthRef.current && !userLoading && canplay) {
      setHandled(true);
      eventHandler();
    }
    return () => {
      if (!mounted.current) {
        sendResume();
      }
    };
  }, [handled, player, playbackAuthRef.current, userLoading, canplay]);
}